<template>
  <div class="editor-container">
    <!-- Classes are needed for tip tap to do its magic -->
    <section
      :id="`editor${_uid}`"
      :ref="`editor${_uid}`"
      class="editor"
      :class="[
        menuBar ? 'editor-menubar' : '',
        hiddenMenu ? 'editor-hiddenMenu' : '',
        bordersClass,
      ]"
    >
      <asc-rich-text-menu-bar
        v-if="menuBar"
        menu-bar
        :editor="editor"
        :can-edit="canEdit"
        :display-link-dialog="displayLinkDialog"
        :display-image-dialog="displayImageDialog"
        :remove-link="removeLink"
        :allow-images="allowImages"
        :allowed-image-types="allowedImageTypes"
        :comment-change-event-on-blur="commentChangeEventOnBlur"
      ></asc-rich-text-menu-bar>

      <editor-content
        :class="[emptyClass, readonlyClass, disabledClass]"
        class="editor__content"
        :editor="editor"
      />

      <asc-rich-text-menu-bar
        v-if="hiddenMenu"
        hidden-menu
        :editor="editor"
        :can-edit="canEdit"
        :display-link-dialog="displayLinkDialog"
        :display-image-dialog="displayImageDialog"
        :remove-link="removeLink"
        :allow-images="allowImages"
        :comment-change-event-on-blur="commentChangeEventOnBlur"
        @isFocused="setIsFocused"
      ></asc-rich-text-menu-bar>
    </section>

    <div v-if="!hideDetails" class="editor__details v-text-field__details">
      <v-counter
        v-if="counter"
        :value="getEditorContentLength"
        :max="maxLength"
      >
      </v-counter>
      <v-messages
        :value="messagesToDisplay"
        :color="hint && hint.length ? '' : validationState"
      ></v-messages>
    </div>

    <ul v-show="showSuggestions" ref="suggestions" class="suggestion-list">
      <template v-if="hasResults">
        <li
          v-for="(user, index) in filteredUsers"
          :key="user.id"
          class="suggestion-list__item"
          :class="{ 'is-selected': navigatedUserIndex === index }"
          @click="selectUser(user)"
        >
          <span class="suggestion-list__title">
            {{ user.firstName }} {{ user.lastName }}
          </span>
          <span class="suggestion-list__subtitle">
            {{ user.userName ? user.userName : '' }}
          </span>
        </li>
      </template>
      <li v-else class="suggestion-list__item is-empty">No Users Found</li>
    </ul>

    <asc-dialog
      :show-dialog="showLinkDialog"
      :on-primary="onAddLinkClicked"
      primary-button-text="Apply"
      class="asc-editor--dialog"
      :primary-available="validLink"
      secondary-button-text="Cancel"
      :on-secondary="onLinkDialogCloseClicked"
      :on-close="onLinkDialogCloseClicked"
    >
      <asc-text-field-input
        v-model="linkUrl"
        label="Destination URL"
      ></asc-text-field-input>
    </asc-dialog>

    <asc-dialog
      :show-dialog="showImageDialog"
      :on-primary="onAddImageClicked"
      primary-button-text="Insert Image"
      :primary-available="imageUploadEnabled"
      :on-secondary="onImageDialogClose"
      secondary-button-text="Close"
      :on-close="onImageDialogClose"
    >
      <asc-file-upload-input
        v-model="images"
        viewable
        :on-upload-file="handleUploadFile"
        :on-remove-file="handleRemoveFile"
        :accepted-file-properties="acceptedFileProperties"
        :allow-file-download="false"
      ></asc-file-upload-input>
    </asc-dialog>
  </div>
</template>

<script>
// Used from TipTap Documentation when implementing suggestions
// https://github.com/scrumpy/tiptap/blob/master/examples/Components/Routes/Suggestions/index.vue
import AscDialog from '@/components/atoms/AscDialog'
import AscRichTextMenuBar from '@/components/atoms/AscRichTextMenuBar'
import AscTextFieldInput from '@/components/atoms/AscTextFieldInput'
import AscFileUploadInput from '@/components/molecules/AscFileUploadInput'
import Image from '@/utilities/image'
import MaxSize from '@/utilities/maxLengthExtension'
import Mention from '@/utilities/mention'
import _ from 'lodash'
import tippy, { sticky } from 'tippy.js'
import { Editor, EditorContent } from 'tiptap'
import {
  Bold,
  BulletList,
  Heading,
  History,
  Italic,
  Link,
  ListItem,
  OrderedList,
  Placeholder,
  TodoItem,
  TodoList,
  Underline,
  Focus,
} from 'tiptap-extensions'
import { v4 as uuidv4 } from 'uuid'

export default {
  name: 'AscRichTextInput',

  components: {
    EditorContent,
    AscDialog,
    AscTextFieldInput,
    AscRichTextMenuBar,
    AscFileUploadInput,
  },
  props: {
    canEdit: {
      type: Boolean,
      default: false,
    },
    clear: {
      type: Boolean,
      default: false,
    },
    comment: {
      type: String,
      default: () => '',
    },
    placeholder: {
      type: String,
      default: () => 'Say something...',
    },
    reset: {
      type: Boolean,
      default: false,
    },
    userList: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    menuBar: {
      type: Boolean,
      default: false,
    },
    hiddenMenu: {
      type: Boolean,
      default: false,
    },
    newComment: {
      type: Boolean,
      default: false,
    },
    canPost: {
      type: Boolean,
      default: false,
    },
    allowImages: {
      type: Boolean,
      default: false,
    },
    allowedImageTypes: {
      type: Array,
      default: () => ['.png', '.jpg', '.jpeg'],
    },
    maxLength: {
      type: Number,
      default: undefined,
    },
    counter: {
      type: Boolean,
      default: false,
    },
    hint: {
      type: String,
      default: undefined,
    },
    hideDetails: {
      type: Boolean,
      default: false,
    },
    commentChangeEventOnBlur: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      messages: [],
      rules: [],
      errorBucket: [],
      valid: false,
      clearText: this.clear,
      filteredUsers: [],
      insertMention: () => {},
      navigatedUserIndex: 0,
      query: null,
      suggestionRange: null,
      text: this.comment,
      editor: new Editor({
        content: this.comment,
        editable: this.canEdit && !this.disabled,
        extensions: [
          new Placeholder({
            emptyEditorClass: 'is-editor-empty',
            emptyNodeClass: 'is-empty',
            emptyNodeText: this.placeholder,
            showOnlyWhenEditable: false,
            showOnlyCurrent: true,
          }),
          new Mention({
            items: () => this.userList,
            // is called when a suggestion starts
            onEnter: ({ items, query, range, command, virtualNode }) => {
              this.query = query
              this.filteredUsers = items
              this.suggestionRange = range
              this.renderPopup(virtualNode)
              this.insertMention = command
            },
            // is called when a suggestion has changed
            onChange: ({ items, query, range, virtualNode }) => {
              this.query = query
              this.filteredUsers = items
              this.suggestionRange = range
              this.navigatedUserIndex = 0
              this.renderPopup(virtualNode)
            },
            // is called when a suggestion is cancelled
            onExit: () => {
              // reset all saved values
              this.query = null
              this.filteredUsers = []
              this.suggestionRange = null
              this.navigatedUserIndex = 0
              this.destroyPopup()
            },
            // is called on every keyDown event while a suggestion is active
            onKeyDown: ({ event }) => {
              if (event.key === 'ArrowUp') {
                this.upHandler()
                return true
              }
              if (event.key === 'ArrowDown') {
                this.downHandler()
                return true
              }
              if (event.key === 'Enter') {
                this.enterHandler()
                return true
              }
              return false
            },
          }),
          new Image({
            allowImages: this.allowImages,
            allowedImageTypes: this.allowedImageTypes,
          }),
          new MaxSize({
            maxSize: this.maxLength,
            emptyComment: '<p></p>',
          }),
          new BulletList(),
          new Heading({ levels: [1, 2, 3] }),
          new ListItem(),
          new OrderedList(),
          new TodoItem(),
          new TodoList(),
          new Link(),
          new Bold(),
          new Italic(),
          new Underline(),
          new History(),
          new Focus(),
        ],
        onUpdate: event => {
          this.validate()
          if (!this.commentChangeEventOnBlur) {
            if (!this.editor.view.editable && event) {
              return false
            }
            this.text = event.getHTML()
            this.$emit('updated', this.text)
          }
        },
        onBlur: event => {
          if (this.commentChangeEventOnBlur) {
            this.validate()
            // If not editable content then dont trigger the event
            if (!event.view.editable && event.event) {
              event.event.stopPropagation()
              return false
            }
            // this covers a special case where, when the user
            // clicks on the menubar buttons (bold, italic, etc.) we don't want
            // to emit the update event, because that causes those button not to work
            // ONLY emit the update event if you truly are clicking completely out of the editor
            const relatedTargetClassList = event.event?.relatedTarget?.classList

            if (
              !relatedTargetClassList?.contains('menubar__button') &&
              !relatedTargetClassList?.contains('tippy-box')
            ) {
              // neeed to delay else getHTML wont be updated
              this.text = this.editor.getHTML()
              this.$emit('updated', this.text)
            }
          }
        },
      }),
      showLinkDialog: false,
      showImageDialog: false,
      linkUrl: null,
      validLink: false,
      images: [],
      acceptedFileProperties: {
        allowedFileTypes: this.allowedImageTypes.join(','),
        maxFileSizeInMB: 5,
      },
      isFocused: false,
      tippySuggestionInstance: null,
      emptyComment: '<p></p>',
    }
  },
  computed: {
    emptyClass() {
      return this.comment === '' ? 'empty' : ''
    },
    hasResults() {
      return !!this.filteredUsers.length
    },
    showSuggestions() {
      return !!this.query || this.hasResults
    },
    readonlyClass() {
      return this.canEdit ? '' : 'readonly'
    },
    disabledClass() {
      return this.disabled ? 'disabled' : ''
    },
    bordersClass() {
      if (this.hiddenMenu && this.canEdit) {
        if (this.isFocused) {
          return 'editor-noBorder'
        } else if (this.newComment || this.canPost) {
          return 'editor-addBorder'
        } else {
          return ''
        }
      } else {
        return ''
      }
    },
    imageUploadEnabled() {
      return (
        this.images.length > 0 &&
        !_.has(
          _.countBy(this.images, image => image.loading),
          true,
        )
      )
    },
    getEditorContent() {
      return this.editor && this.editor.state.doc.textContent
        ? this.editor.state.doc.textContent
        : ''
    },
    getEditorContentLength() {
      const html = this.editor.getHTML()
      if (html !== this.emptyComment) {
        const emptyParagraphOccurences = (html.match(/<p><\/p>/g) || []).length
        return this.editor && this.getEditorContent.length
          ? this.getEditorContent.length + emptyParagraphOccurences
          : 0
      }
      return 0
    },
    validationState() {
      if (this.disabled) return undefined
      if (!this.valid) return 'error'
      return undefined
    },
    getRules() {
      const rules = []
      if (this.maxLength)
        rules.push(
          v =>
            (v || '').length <= this.maxLength ||
            `A maximum of ${this.maxLength} characters is allowed`,
        )

      return rules
    },
    messagesToDisplay() {
      if (this.hint) return [this.hint]

      return this.messages
        .map(validation => {
          if (typeof validation === 'string') return validation

          const validationResult = validation(this.getEditorContent)

          return typeof validationResult === 'string' ? validationResult : ''
        })
        .filter(message => message !== '')
    },
  },

  watch: {
    linkUrl(newValue) {
      this.validLink =
        !!newValue &&
        !!newValue.trim() &&
        newValue !== this.editor.activeMarkAttrs.link.href
      if (this.validLink) {
        this.linkUrl = newValue.trim()
      }
    },
    canEdit(newValue) {
      this.editor.setOptions({
        editable: newValue,
      })
      if (this.canEdit) {
        this.editor.focus()
        let editorHtml = this.editor.getHTML()
        editorHtml += ' '
        this.editor.setContent(editorHtml)
      }
    },
    clear(newValue) {
      this.clearText = newValue
      if (this.clearText) this.clearContent()
    },
    comment() {
      this.text = this.editor.getHTML()
    },
    isFocused(newValue) {
      if (
        !newValue &&
        this.canEdit &&
        !this.newComment &&
        !this.canPost &&
        this.hiddenMenu
      ) {
        // this.$emit('disableEdit')
      }
    },
  },

  mounted() {
    this.$nextTick(() => {
      const editor = document.querySelector(`#editor${this._uid}`)
      const selector = editor.querySelectorAll('.mention[id]')
      this.createTooltip(selector)
    })
    window.addEventListener('scroll', this.handleScroll, true)
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll, true)
    this.destroyPopup()
    this.editor.destroy()
  },

  methods: {
    handleScroll(event) {
      const editor = document.querySelector(`#editor${this._uid}`)
      const suggestions = this.$refs.suggestions
      if (
        this.tippySuggestionInstance?.[0] &&
        !editor.contains(event.target) &&
        !(suggestions && suggestions.contains(event.target))
      ) {
        this.query = null
        this.filteredUsers = []
        this.suggestionRange = null
        this.navigatedUserIndex = 0
        this.destroyPopup()
      }
    },
    setIsFocused(data) {
      this.isFocused = data
    },
    displayLinkDialog() {
      this.linkUrl = this.editor.activeMarkAttrs.link.href
      this.showLinkDialog = true
      this.$emit('editorDialogOpen', this.showLinkDialog)
    },
    displayImageDialog() {
      this.linkUrl = this.editor.activeMarkAttrs.link.href
      this.showImageDialog = true
    },
    onAddLinkClicked() {
      const link =
        this.linkUrl.includes('http://') || this.linkUrl.includes('https://')
          ? this.linkUrl
          : `http://${this.linkUrl}`
      this.editor.commands.link({ href: link })
      this.showLinkDialog = false
      this.$emit('editorDialogOpen', this.showLinkDialog)
    },
    onLinkDialogCloseClicked() {
      this.linkUrl = null
      this.validLink = false
      this.showLinkDialog = false
      this.$emit('editorDialogOpen', this.showLinkDialog)
    },
    onImageDialogClose() {
      this.images = []
      this.showImageDialog = false
    },
    handleUploadFile(file) {
      const reader = new FileReader()
      reader.onload = readerEvent => {
        const index = _.findIndex(this.images, { displayName: file.name })
        this.images[index].src = readerEvent.target.result
        this.images[index].loading = false
      }
      reader.readAsDataURL(file)
    },
    onAddImageClicked() {
      for (let i = 0; i < this.images.length; i++) {
        this.editor.commands.image({
          src: this.images[i].src,
        })
      }

      this.onImageDialogClose()
    },
    handleRemoveFile(file) {
      this.images = _.reject(this.images, {
        displayName: file.displayName,
      })
    },
    removeLink() {
      this.editor.commands.link({ href: null })
    },
    clearContent() {
      this.editor.clearContent()
      this.$emit('updated', '')
    },
    upHandler() {
      this.navigatedUserIndex =
        (this.navigatedUserIndex + this.filteredUsers.length - 1) %
        this.filteredUsers.length
    },
    downHandler() {
      this.navigatedUserIndex =
        (this.navigatedUserIndex + 1) % this.filteredUsers.length
    },
    enterHandler() {
      const user = this.filteredUsers[this.navigatedUserIndex]
      if (user) {
        this.selectUser(user)
      }
    },
    createTooltip(selector) {
      tippy(selector, {
        content(reference) {
          const label = reference.getAttribute('data-mention-label')
          const email = reference.getAttribute('data-mention-email')
          let content = ''
          if (label) content = `<strong>${label}</strong>`
          if (email) content += `<small>${email}</small>`
          return content
        },
        theme: 'ascTippy',
        offset: [0, 2],
        duration: [300, 100],
        allowHTML: true,
        inertia: true,
        placement: 'bottom-start',
      })
    },
    selectUser(user) {
      const uid = uuidv4()
      this.insertMention({
        range: this.suggestionRange,
        attrs: {
          id: user.userId,
          uid: uid,
          label: `${user.firstName} ${user.lastName}`,
          email: `${user.userName ? user.userName : ''}`,
        },
      })

      if (user && user.userId && uid) {
        this.createTooltip(`#editor${this._uid} .mention[id="${uid}"]`)
      }
      this.editor.focus()
    },
    // renders a popup with suggestions
    // tiptap provides a virtualNode object for using popper.js (or tippy.js) for popups
    renderPopup(node) {
      const boundingClientRect = node.getBoundingClientRect()
      const { x, y } = boundingClientRect

      if (x === 0 && y === 0) {
        return
      }

      if (this.popup) {
        return
      }
      // ref: https://atomiks.github.io/tippyjs/v6/all-props/
      // Target will need to be updated when this is used in a template
      this.tippySuggestionInstance = tippy(`#editor${this._uid}`, {
        getReferenceClientRect: () => boundingClientRect,
        appendTo: () => document.body,
        interactive: true,
        sticky: true,
        plugins: [sticky],
        content: this.$refs.suggestions,
        trigger: 'manual',
        showOnCreate: true,
        placement: 'bottom-start',
        inertia: true,
        duration: [400, 200],
        moveTransition: 'transform 0.2s ease-out',
      })
    },
    destroyPopup() {
      if (this.tippySuggestionInstance && this.tippySuggestionInstance[0]) {
        this.tippySuggestionInstance[0].destroy()
        this.tippySuggestionInstance = null
      }
    },
    validate(value = this.getEditorContent) {
      const errorBucket = []

      for (let index = 0; index < this.getRules.length; index++) {
        const rule = this.getRules[index]
        const valid = typeof rule === 'function' ? rule(value) : rule

        if (valid === false || typeof valid === 'string') {
          errorBucket.push(valid || '')
        }
      }

      this.messages = errorBucket
      this.valid = errorBucket.length === 0
      this.$emit('valid', this.valid)
      return this.valid
    },
  },
}
</script>

<style lang="scss">
.asc-editor--dialog {
  .v-input__slot {
    top: 20px;
  }
}

.menubar {
  display: flex;
  width: 100%;

  &__button {
    margin: 12px 1px;
    padding: 0 3px;
    width: 25px;
    height: 25px;

    &:focus {
      outline: $accent-200;
    }

    &:hover {
      background-color: $accent-100;
      border-radius: 3px;
    }

    &.is-active {
      background-color: $accent-200;
      border-radius: 3px;
    }

    &.disabled:hover {
      background-color: transparent;
    }
  }
}

.rich-text-select {
  width: 300px;
}

.editor {
  &__content {
    position: relative;

    & .ProseMirror {
      border-bottom: 1px solid $gray-500;
      padding: 8px 0;

      &:hover {
        border-bottom: 1px solid $gray-900;
      }

      &::after {
        background: none repeat scroll 0 0 transparent;
        bottom: 0;
        content: '';
        display: block;
        height: 1px;
        left: 50%;
        position: absolute;
        transition: width 0.3s ease 0s, left 0.3s ease 0s;
        width: 0;
      }

      ::selection {
        background-color: $accent-200;
      }

      & p {
        margin: 0;

        &.is-editor-empty:first-child::before {
          content: attr(data-empty-text);
          float: left;
          color: $gray-600;
          pointer-events: none;
          height: 0;
        }

        & a {
          @include link-styling;
        }
      }

      img {
        max-width: 100%;
      }

      & span.mention {
        color: $accent-700;
        cursor: default;
        position: relative;
        display: inline-block;
      }
    }

    & > .ProseMirror-focused:focus {
      border-bottom: none;
      outline: none;

      &::after {
        background: $accent-700;
        left: 0;
        width: 100%;
      }
    }

    &.readonly > .ProseMirror {
      border-bottom: none;

      &:focus::after {
        display: none;
      }
    }

    &.disabled {
      cursor: not-allowed;

      > .ProseMirror-focused {
        &::after {
          background: none;
        }
      }
    }
  }

  &-menubar {
    border: 1px solid $gray-300;

    & .menubar {
      padding: 15px;
      background-color: $gray-200;
    }

    & > .editor__content {
      & .ProseMirror {
        border-bottom: none;
        padding: 10px;

        & p {
          font-size: 14px;
          line-height: 18px;
        }

        & h1 {
          font-size: 22px;
          line-height: 26px;
          font-weight: normal;

          & a {
            @include link-styling;
          }
        }

        & h2 {
          font-size: 16px;
          line-height: 22px;
          font-weight: normal;

          & a {
            @include link-styling;
          }
        }

        & h3 {
          font-size: 14px;
          line-height: 18px;
          font-weight: normal;
          color: $gray-700;

          & a {
            @include link-styling;
          }
        }

        &-focused:focus {
          border: none;
          outline: none;

          &::after {
            background: transparent;
            left: 0;
            width: 100%;
          }
        }
      }
    }
  }

  &-hiddenMenu {
    & .editor__content {
      & .ProseMirror {
        padding: 8px 0;

        &-focused:focus {
          border: none;
          outline: none;

          &::after {
            background: transparent;
            left: 0;
            width: 100%;
          }
        }
      }
    }
  }

  &-noBorder > .editor__content > .ProseMirror {
    border-bottom: none;
  }

  &-addBorder > .editor__content > .ProseMirror {
    border-bottom: 1px solid $gray-500;

    &:hover {
      border-bottom: 1px solid $gray-900;
    }
  }
}

.editor__details {
  text-align: left;
  color: $gray-700;
  font-size: 12px;
  letter-spacing: 0;
  line-height: 18px;
  margin-top: 8px;
  flex-direction: column;

  & > .v-messages {
    margin-top: 2px;
  }
}

.suggestion-list {
  background: $primary-white;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.17);
  list-style: none;
  max-height: 316px;
  max-width: 242px;
  width: 239px;
  overflow-y: auto;
  padding: 0 !important;
  position: relative;
  font-family: Roboto, sans-serif;
  line-height: 1.5;

  &__item {
    display: flex;
    flex-flow: column nowrap;
    padding: 6px 13px;
    max-height: 52px;
    cursor: pointer;

    &.is-empty {
      color: $primary-black;
      line-height: 18px;
    }

    &:not(.is-empty) {
      &:hover,
      &.is-selected {
        background-color: $gray-100;
      }
    }
  }

  &__title {
    font-weight: 500;
    color: $primary-black;
    font-size: 13px;
    text-transform: capitalize;
    line-height: 18px;
  }

  &__subtitle {
    color: $gray-600;
    font-size: 12px;
    line-height: 18px;
  }
}

.tippy-box[data-theme~='ascTippy'] {
  max-height: 52px;
  border-radius: 0 0 2px 2px;
  padding: 6px 13px;
  background-color: $primary-white;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.17);
  letter-spacing: 0;
  line-height: 18px;
  font-family: Roboto, sans-serif;

  strong {
    display: block;
    color: $primary-black;
    font-size: 14px;
  }

  small {
    margin-top: 2px;
    color: $gray-600;
    font-size: 12px;
  }
}
</style>
